import { zodResolver } from "@hookform/resolvers/zod";
import { type LoginRequest, isMfaChallenge } from "@seagames/contracts";
import { armBrandSplash } from "@seagames/ui";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { type ReactNode, useId, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import QRCode from "react-qr-code";
import { z } from "zod";
import {
	CardHeading,
	Checkbox,
	ErrorNotice,
	HelpLine,
	InfoNotice,
	LinkButton,
	PasswordToggle,
	SubmitButton,
	TextField,
	codeInputClass,
	fieldIconClass,
	fieldInputClass,
} from "../components/auth/auth-form";
import { KeyGlyph, UserGlyph } from "../components/auth/auth-icons";
import { AuthCard, AuthSplit } from "../components/auth/auth-split";
import { ApiError } from "../lib/api/client";
import { forgetIdentifier, readRememberedIdentifier, rememberIdentifier } from "../lib/auth/remembered-identifier";
import { useLoginFormSchema, useMfaVerifyFormSchema } from "../lib/i18n/use-form-schemas";
import {
	type MfaSetup,
	acceptLogin,
	beginMfaSetup,
	confirmMfaSetup,
	isCompletedLogin,
	login,
	verifyMfa,
} from "../lib/auth/session";

/**
 * Sign-in — the designer's prototype (SEAGAMES-UI `#/login`, `.lgn*`): the
 * split shell is AuthSplit, the card and its parts are auth-form.tsx.
 *
 * The prototype only draws the credentials step; the 2FA and backup-code
 * stages are the same card with different contents. Our rules beat its copy:
 * the identifier is "username or email", there is no "Forgot password?" link
 * (no self-service reset exists — an administrator issues a temporary
 * password, which the help line says), and no prototype account hints.
 *
 * Search params are validated, not trusted: `redirect` comes back from the
 * router after a bounced navigation, and an unvalidated one is an open-redirect
 * — hand it an absolute URL and the "sign in" button walks the user to another
 * origin carrying whatever the page shows next.
 */
const loginSearch = z.object({
	/** Path only. Anything with a scheme or a protocol-relative prefix is dropped. */
	redirect: z
		.string()
		.optional()
		.transform((v) => (v && v.startsWith("/") && !v.startsWith("//") ? v : undefined)),
	reason: z.enum(["revoked", "expired", "user"]).optional(),
});

export const Route = createFileRoute("/login")({
	validateSearch: loginSearch,
	component: LoginPage,
});

/**
 * The designer's "Are you a volunteer? Log in here" points at the volunteer
 * portal, which is a separate system. Until its address is configured the row
 * is not drawn — a link to nowhere is worse than no link.
 */
const VOLUNTEER_PORTAL_URL: string | undefined = import.meta.env.VITE_VOLUNTEER_PORTAL_URL;

type Stage =
	| { kind: "credentials" }
	| { kind: "mfa-verify"; ticket: string; name: string }
	| { kind: "mfa-enroll"; ticket: string; setup: MfaSetup }
	| { kind: "backup-codes"; codes: string[]; mustChangePassword: boolean };

function LoginPage() {
	const { redirect, reason } = Route.useSearch();
	const navigate = useNavigate();
	const [stage, setStage] = useState<Stage>({ kind: "credentials" });

	/** The one exit from this page. `must_change_password` is a server verdict. */
	const finish = (mustChangePassword: boolean) => {
		// The shell plays the WSL GMS splash once it mounts (after a forced password change too).
		armBrandSplash();
		void navigate({
			to: mustChangePassword ? "/change-password" : (redirect ?? "/"),
			replace: true,
		});
	};

	return (
		<AuthSplit>
			<AuthCard>
				{stage.kind === "credentials" && (
					<CredentialsStep
						onStage={setStage}
						onDone={finish}
						notice={reason ? <SignedOutNotice reason={reason} /> : null}
					/>
				)}
				{stage.kind === "mfa-verify" && (
					<MfaVerifyStep
						ticket={stage.ticket}
						name={stage.name}
						onDone={finish}
						onRestart={() => setStage({ kind: "credentials" })}
					/>
				)}
				{stage.kind === "mfa-enroll" && (
					<MfaEnrollStep
						ticket={stage.ticket}
						setup={stage.setup}
						onStage={setStage}
						onRestart={() => setStage({ kind: "credentials" })}
					/>
				)}
				{stage.kind === "backup-codes" && (
					<BackupCodesStep codes={stage.codes} onDone={() => finish(stage.mustChangePassword)} />
				)}
			</AuthCard>
		</AuthSplit>
	);
}

function SignedOutNotice({ reason }: { reason: "revoked" | "expired" | "user" }) {
	const { t } = useTranslation("auth");
	if (reason === "user") return null;
	return <InfoNotice>{reason === "expired" ? t("reasons.expired") : t("reasons.revoked")}</InfoNotice>;
}

// ── 1. Username + password ──────────────────────────────────────────────────

function CredentialsStep({
	onStage,
	onDone,
	notice,
}: {
	onStage: (s: Stage) => void;
	onDone: (mustChangePassword: boolean) => void;
	/** The signed-out notice — drawn under the heading, where the error banner goes. */
	notice: ReactNode;
}) {
	const { t } = useTranslation("auth");
	const identifierId = useId();
	const passwordId = useId();
	const [error, setError] = useState<unknown>(null);
	const [remembered] = useState(readRememberedIdentifier);
	const [remember, setRemember] = useState(() => remembered !== "");
	const [showPassword, setShowPassword] = useState(false);
	const loginSchema = useLoginFormSchema();
	const {
		register,
		handleSubmit,
		formState: { errors, isSubmitting },
	} = useForm<LoginRequest>({
		resolver: zodResolver(loginSchema),
		defaultValues: { identifier: remembered, password: "" },
	});

	const onSubmit = handleSubmit(async (values) => {
		setError(null);
		try {
			const result = await login(values);

			// Only once the server has accepted it — remembering a typo would hand
			// the typo back on every future visit.
			if (remember) rememberIdentifier(values.identifier);
			else forgetIdentifier();

			// 2FA is decided per ROLE at sign-in, so a challenge is a normal
			// outcome, not an error. No tokens have been issued yet.
			if (isMfaChallenge(result)) {
				if (result.mfa_stage === "verify") {
					onStage({ kind: "mfa-verify", ticket: result.mfa_ticket, name: result.user.name });
				} else {
					const setup = await beginMfaSetup(result.mfa_ticket);
					onStage({ kind: "mfa-enroll", ticket: result.mfa_ticket, setup });
				}
				return;
			}

			acceptLogin(result);
			onDone(result.must_change_password);
		} catch (err) {
			setError(err);
		}
	});

	return (
		<>
			<CardHeading title={t("login.title")}>{t("login.subtitle")}</CardHeading>

			{notice}
			<ErrorNotice error={error} />

			<form onSubmit={onSubmit} noValidate className="grid gap-[1rem]">
				<TextField
					label={t("login.identifier.label")}
					htmlFor={identifierId}
					error={errors.identifier?.message}
					icon={<UserGlyph className={fieldIconClass} />}
				>
					<input
						{...register("identifier")}
						id={identifierId}
						type="text"
						autoComplete="username"
						autoCapitalize="none"
						spellCheck={false}
						autoFocus
						placeholder={t("login.identifier.placeholder")}
						aria-invalid={errors.identifier ? "true" : undefined}
						className={fieldInputClass}
					/>
				</TextField>

				<TextField
					label={t("login.password.label")}
					htmlFor={passwordId}
					error={errors.password?.message}
					icon={<KeyGlyph className={fieldIconClass} />}
					trailing={
						<PasswordToggle
							shown={showPassword}
							onToggle={() => setShowPassword((v) => !v)}
							controls={passwordId}
						/>
					}
				>
					<input
						{...register("password")}
						id={passwordId}
						type={showPassword ? "text" : "password"}
						autoComplete="current-password"
						placeholder={t("login.password.placeholder")}
						aria-invalid={errors.password ? "true" : undefined}
						className={fieldInputClass}
					/>
				</TextField>

				<Checkbox checked={remember} onChange={setRemember}>
					{t("login.rememberMe")}
				</Checkbox>

				<SubmitButton pending={isSubmitting} chevron>
					{t("login.submit")}
				</SubmitButton>

				{VOLUNTEER_PORTAL_URL && (
					<div className="flex items-center gap-[0.625rem] text-center text-[0.8125rem] text-gms-ink-2">
						<span aria-hidden className="h-px flex-1 bg-gms-line" />
						<span>
							{t("login.volunteer.prompt")}{" "}
							<a
								href={VOLUNTEER_PORTAL_URL}
								className="rounded-[0.25rem] font-semibold text-gms-navy underline-offset-[3px] hover:underline focus-visible:outline-2 focus-visible:outline-gms-navy focus-visible:outline-offset-2"
							>
								{t("login.volunteer.link")}
							</a>
						</span>
						<span aria-hidden className="h-px flex-1 bg-gms-line" />
					</div>
				)}

				{/* There is no self-service reset: an administrator issues a temporary password. */}
				<HelpLine>{t("login.help")}</HelpLine>
			</form>
		</>
	);
}

// ── 2a. Existing enrolment: enter the code ──────────────────────────────────

function MfaVerifyStep({
	ticket,
	name,
	onDone,
	onRestart,
}: {
	ticket: string;
	name: string;
	onDone: (mustChangePassword: boolean) => void;
	onRestart: () => void;
}) {
	const { t } = useTranslation("auth");
	const codeId = useId();
	const [error, setError] = useState<unknown>(null);
	const mfaSchema = useMfaVerifyFormSchema();
	const {
		register,
		handleSubmit,
		formState: { errors, isSubmitting },
	} = useForm<{ mfa_ticket: string; code: string }>({
		resolver: zodResolver(mfaSchema),
		defaultValues: { mfa_ticket: ticket, code: "" },
	});

	const onSubmit = handleSubmit(async (values) => {
		setError(null);
		try {
			const result = await verifyMfa(values);
			acceptLogin(result);
			onDone(result.must_change_password);
		} catch (err) {
			setError(err);
			// The ticket is spent once used and expires on its own. Anything that
			// invalidates it means starting from the password again.
			if (err instanceof ApiError && err.code === "MFA_TICKET_INVALID") onRestart();
		}
	});

	return (
		<>
			<CardHeading title={t("mfa.verify.title")}>{t("mfa.verify.subtitle", { name })}</CardHeading>

			<ErrorNotice error={error} />

			<form onSubmit={onSubmit} noValidate className="grid gap-[1rem]">
				<input type="hidden" {...register("mfa_ticket")} />
				<TextField
					label={t("mfa.verify.code.label")}
					htmlFor={codeId}
					error={errors.code?.message}
					hint={t("mfa.verify.code.hint")}
				>
					<input
						{...register("code")}
						id={codeId}
						type="text"
						inputMode="numeric"
						autoComplete="one-time-code"
						autoFocus
						maxLength={12}
						placeholder={t("mfa.codePlaceholder")}
						aria-invalid={errors.code ? "true" : undefined}
						className={codeInputClass}
					/>
				</TextField>

				<SubmitButton pending={isSubmitting}>{t("mfa.verify.submit")}</SubmitButton>
				<LinkButton onClick={onRestart}>{t("mfa.backToSignIn")}</LinkButton>
			</form>
		</>
	);
}

// ── 2b. Role requires 2FA and there is no enrolment yet ─────────────────────

function MfaEnrollStep({
	ticket,
	setup,
	onStage,
	onRestart,
}: {
	ticket: string;
	setup: MfaSetup;
	onStage: (s: Stage) => void;
	onRestart: () => void;
}) {
	const { t } = useTranslation("auth");
	const codeId = useId();
	const [error, setError] = useState<unknown>(null);
	const [code, setCode] = useState("");
	const [busy, setBusy] = useState(false);

	const onSubmit = async (event: React.FormEvent) => {
		event.preventDefault();
		setError(null);
		setBusy(true);
		try {
			const result = await confirmMfaSetup(ticket, code.trim());

			// Confirming with a ticket also completes the sign-in, so the response
			// carries the tokens. Accept them BEFORE showing the backup codes —
			// otherwise a closed tab loses both the codes and the session.
			let mustChangePassword = false;
			if (isCompletedLogin(result)) {
				acceptLogin(result);
				mustChangePassword = result.must_change_password;
			}

			onStage({ kind: "backup-codes", codes: result.backup_codes, mustChangePassword });
		} catch (err) {
			setError(err);
			if (err instanceof ApiError && err.code === "MFA_TICKET_INVALID") onRestart();
		} finally {
			setBusy(false);
		}
	};

	return (
		<>
			<CardHeading title={t("mfa.enroll.title")}>{t("mfa.enroll.subtitle")}</CardHeading>

			<ErrorNotice error={error} />

			<div className="flex flex-col items-center gap-[0.75rem] rounded-[0.5rem] border border-gms-line bg-gms-soft p-[1rem]">
				{/* White plate behind the QR: scanners need the light quiet zone. */}
				<div className="rounded-[0.5rem] bg-white p-[0.75rem]">
					<QRCode value={setup.otpauth_uri} size={168} />
				</div>
				<div className="text-center">
					<p className="text-[0.75rem] text-gms-ink-3">{t("mfa.enroll.manualEntry")}</p>
					<code className="select-all break-all font-mono text-[0.75rem]">{setup.secret}</code>
				</div>
			</div>

			<form onSubmit={onSubmit} noValidate className="grid gap-[1rem]">
				<TextField label={t("mfa.enroll.code.label")} htmlFor={codeId}>
					<input
						id={codeId}
						type="text"
						inputMode="numeric"
						autoComplete="one-time-code"
						maxLength={6}
						placeholder={t("mfa.codePlaceholder")}
						value={code}
						onChange={(e) => setCode(e.target.value)}
						className={codeInputClass}
					/>
				</TextField>

				<SubmitButton pending={busy} disabled={code.trim().length < 6}>
					{t("mfa.enroll.submit")}
				</SubmitButton>
				<LinkButton onClick={onRestart}>{t("mfa.backToSignIn")}</LinkButton>
			</form>
		</>
	);
}

// ── 3. Backup codes, shown exactly once ─────────────────────────────────────

function BackupCodesStep({ codes, onDone }: { codes: string[]; onDone: () => void }) {
	const { t } = useTranslation("auth");
	const [acknowledged, setAcknowledged] = useState(false);

	return (
		<>
			<CardHeading title={t("mfa.backupCodes.title")}>{t("mfa.backupCodes.subtitle")}</CardHeading>

			<div className="grid gap-[0.75rem]">
				<ul className="tabular grid grid-cols-2 gap-[0.5rem] rounded-[0.5rem] border border-gms-line bg-gms-soft p-[1rem] font-mono text-[0.875rem]">
					{codes.map((code) => (
						<li key={code} className="select-all">
							{code}
						</li>
					))}
				</ul>
				<button
					type="button"
					className="cursor-pointer justify-self-start rounded-[0.25rem] font-semibold text-[0.84375rem] text-gms-navy underline-offset-[3px] hover:underline focus-visible:outline-2 focus-visible:outline-gms-navy focus-visible:outline-offset-2"
					onClick={() => void navigator.clipboard?.writeText(codes.join("\n"))}
				>
					{t("mfa.backupCodes.copyAll")}
				</button>
			</div>

			<Checkbox checked={acknowledged} onChange={setAcknowledged}>
				{t("mfa.backupCodes.acknowledge")}
			</Checkbox>

			<SubmitButton type="button" disabled={!acknowledged} onClick={onDone}>
				{t("mfa.backupCodes.continue")}
			</SubmitButton>
		</>
	);
}
